import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DAY_FULL_NAMES,
  type DayMenu,
  type MealSlot,
} from "@/hooks/useFoodMenu";
import { MealSlotRow } from "./MealSlotRow";

interface Props {
  day: DayMenu;
  onUpdate: (day: DayMenu) => void;
  onCopyFrom: (fromDayOfWeek: number) => void;
}

const emptySlot = (sortOrder: number): MealSlot => ({
  label: "",
  timeOfDay: "",
  items: [],
  sortOrder,
  allowOthers: false,
  isAvailable: true,
  servingCapacity: 4,
});

export const DayMenuPanel = ({ day, onUpdate, onCopyFrom }: Props) => {
  const patch = (slots: MealSlot[]) => onUpdate({ ...day, slots });

  const updateSlot = (idx: number, slot: MealSlot) =>
    patch(day.slots.map((s, i) => (i === idx ? slot : s)));

  const addSlot = () => patch([...day.slots, emptySlot(day.slots.length)]);

  const removeSlot = (idx: number) =>
    patch(day.slots.filter((_, i) => i !== idx));

  const otherDays = DAY_FULL_NAMES.map((name, i) => ({
    name,
    dayOfWeek: i,
  })).filter((d) => d.dayOfWeek !== day.dayOfWeek);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Meal slots for {DAY_FULL_NAMES[day.dayOfWeek]}.
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Copy from:</span>
          <Select onValueChange={(v) => onCopyFrom(Number(v))}>
            <SelectTrigger className="w-36 h-8 text-sm">
              <SelectValue placeholder="Select day…" />
            </SelectTrigger>
            <SelectContent>
              {otherDays.map((d) => (
                <SelectItem key={d.dayOfWeek} value={String(d.dayOfWeek)}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {day.slots.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-card">
          No meal slots for this day. Add one below or copy from another day.
        </p>
      )}

      {day.slots.map((slot, si) => (
        <MealSlotRow
          key={si}
          slot={slot}
          removable={day.slots.length > 1}
          onChange={(updated) => updateSlot(si, updated)}
          onRemove={() => removeSlot(si)}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addSlot}
        className="w-full border-dashed"
      >
        <Plus className="w-4 h-4 mr-2" /> Add meal slot
      </Button>
    </div>
  );
};
